import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import KeyRounded from '@mui/icons-material/KeyRounded';
import PhonelinkLockRounded from '@mui/icons-material/PhonelinkLockRounded';
import QrCode2Rounded from '@mui/icons-material/QrCode2Rounded';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { api, unwrap } from '../../api/client';
import type { MfaSetup } from '../../api/m19';
import { useAuth } from '../../auth/AuthProvider';
import { accountErrorMessage, groupSecret } from './messages';
import { QrCode } from './QrCode';

type Stage =
  { step: 'intro' } | { step: 'scan'; setup: MfaSetup } | { step: 'codes'; codes: string[] };

async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function downloadCodes(email: string, codes: string[]) {
  const text = [
    'PackPilot by PackBytes — platform console recovery codes',
    `Account: ${email}`,
    `Created: ${new Date().toISOString()}`,
    '',
    'Each code works once. Keep them somewhere safe and private.',
    '',
    ...codes,
    '',
  ].join('\n');
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'packpilot-recovery-codes.txt';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * TOTP enrolment: start (pending secret) → scan QR / type the secret → confirm a code → save the
 * ten recovery codes. `onDone` runs after the admin confirms the codes are saved.
 */
export function MfaSetupFlow({ onDone }: { onDone: () => void }) {
  const { me, reloadMe } = useAuth();
  const [stage, setStage] = useState<Stage>({ step: 'intro' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState('');
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const start = async () => {
    setError(null);
    setBusy(true);
    try {
      const setup = await unwrap(api.POST('/platform/me/mfa/setup'));
      setCode('');
      setStage({ step: 'scan', setup });
    } catch (e) {
      setError(accountErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const enable = async (value: string) => {
    setError(null);
    setBusy(true);
    try {
      const { recoveryCodes } = await unwrap(
        api.POST('/platform/me/mfa/enable', { body: { code: value } }),
      );
      setStage({ step: 'codes', codes: recoveryCodes });
    } catch (e) {
      setError(accountErrorMessage(e));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    setBusy(true);
    await reloadMe().catch(() => undefined);
    setBusy(false);
    onDone();
  };

  const flash = async (key: string, text: string) => {
    if (await copy(text)) {
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1800);
    }
  };

  const active = stage.step === 'intro' ? 0 : stage.step === 'scan' ? 1 : 2;

  return (
    <Box>
      <Stepper activeStep={active} alternativeLabel sx={{ mb: 3.5 }}>
        {['Start', 'Scan & confirm', 'Save recovery codes'].map((l) => (
          <Step key={l}>
            <StepLabel>{l}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {error}
        </Alert>
      )}

      {stage.step === 'intro' && (
        <Stack spacing={2.5}>
          {[
            {
              icon: <PhonelinkLockRounded />,
              title: 'Install an authenticator app',
              body: 'Google Authenticator, Microsoft Authenticator, 1Password, Authy — any TOTP app works.',
            },
            {
              icon: <QrCode2Rounded />,
              title: 'Scan the QR code',
              body: 'The app then shows a new 6-digit code every 30 seconds.',
            },
            {
              icon: <KeyRounded />,
              title: 'Keep your recovery codes',
              body: 'Ten single-use codes get you in if you lose your phone.',
            },
          ].map((s) => (
            <Stack key={s.title} direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
              <Box
                sx={(t) => {
                  const p = t.vars?.palette.primary.main ?? t.palette.primary.main;
                  return {
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    borderRadius: '12px',
                    display: 'grid',
                    placeItems: 'center',
                    color: 'primary.main',
                    bgcolor: `color-mix(in srgb, ${p} 10%, transparent)`,
                    '& svg': { fontSize: 20 },
                  };
                }}
              >
                {s.icon}
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                  {s.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {s.body}
                </Typography>
              </Box>
            </Stack>
          ))}
          <Box>
            <Button variant="contained" size="large" loading={busy} onClick={() => void start()}>
              Begin setup
            </Button>
          </Box>
        </Stack>
      )}

      {stage.step === 'scan' && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 3, md: 4 }}>
          <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
            <QrCode
              value={stage.setup.otpauthUrl}
              label="QR code for your authenticator app"
              size={196}
            />
            <Link
              href={stage.setup.otpauthUrl}
              variant="caption"
              underline="hover"
              sx={{ fontWeight: 600 }}
            >
              On this phone? Open in your authenticator app
            </Link>
          </Stack>
          <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 650, mb: 0.5 }}>
                Scan with your authenticator app
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Can’t scan? Choose “enter a setup key” in the app and type this key (time-based):
              </Typography>
            </Box>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
                p: 1.5,
                pl: 2,
                borderRadius: '12px',
                bgcolor: 'action.hover',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography
                data-testid="mfa-secret"
                sx={{
                  flex: 1,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                  fontSize: 15,
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  wordBreak: 'break-all',
                }}
              >
                {groupSecret(stage.setup.secret)}
              </Typography>
              <Tooltip title={copied === 'secret' ? 'Copied' : 'Copy key'}>
                <Button
                  size="small"
                  variant="outlined"
                  aria-label="Copy setup key"
                  onClick={() => void flash('secret', stage.setup.secret)}
                  sx={{ minWidth: 0, px: 1.25 }}
                >
                  {copied === 'secret' ? (
                    <CheckCircleRounded fontSize="small" color="success" />
                  ) : (
                    <ContentCopyRounded fontSize="small" />
                  )}
                </Button>
              </Tooltip>
            </Stack>
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                if (code.length === 6 && !busy) void enable(code);
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  size="medium"
                  label="6-digit code from the app"
                  placeholder="000000"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCode(digits);
                    if (digits.length === 6 && !busy) void enable(digits);
                  }}
                  slotProps={{
                    htmlInput: {
                      inputMode: 'numeric',
                      maxLength: 6,
                      style: { letterSpacing: '0.35em', fontVariantNumeric: 'tabular-nums' },
                    },
                  }}
                  sx={{ maxWidth: { sm: 260 } }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  loading={busy}
                  disabled={code.length !== 6}
                >
                  Turn on
                </Button>
              </Stack>
            </form>
            <Box>
              <Button size="small" onClick={() => void start()} disabled={busy}>
                Get a new QR code
              </Button>
            </Box>
          </Stack>
        </Stack>
      )}

      {stage.step === 'codes' && (
        <Stack spacing={2.5}>
          <Alert severity="success" icon={<CheckCircleRounded />}>
            Two-step verification is on. Save these recovery codes now — they are shown only once.
          </Alert>
          <Box
            data-testid="recovery-codes"
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' },
              gap: 1,
              p: 2,
              borderRadius: '14px',
              border: '1px dashed',
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            {stage.codes.map((c) => (
              <Typography
                key={c}
                component="code"
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                  fontSize: 14,
                  fontWeight: 600,
                  textAlign: 'center',
                  py: 0.75,
                  borderRadius: '8px',
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                {c}
              </Typography>
            ))}
          </Box>
          <Stack direction="row" spacing={1.25} sx={{ flexWrap: 'wrap', rowGap: 1.25 }}>
            <Button
              variant="outlined"
              startIcon={
                copied === 'codes' ? <CheckCircleRounded color="success" /> : <ContentCopyRounded />
              }
              onClick={() => void flash('codes', stage.codes.join('\n'))}
            >
              {copied === 'codes' ? 'Copied' : 'Copy all'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadRounded />}
              onClick={() => downloadCodes(me?.email ?? '', stage.codes)}
            >
              Download .txt
            </Button>
          </Stack>
          <FormControlLabel
            control={<Checkbox checked={saved} onChange={(e) => setSaved(e.target.checked)} />}
            label="I have saved my recovery codes somewhere safe"
          />
          <Box>
            <Button
              variant="contained"
              size="large"
              disabled={!saved}
              loading={busy}
              onClick={() => void finish()}
            >
              Done
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
